import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { FileText, Calendar, User, BarChart3 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { getIdToken } from "@/lib/auth";

interface Product {
  id: string;
  name: string;
  description: string;
}

interface QuestionOption {
  id?: string;
  text: string;
  value: string;
  order: number;
}

interface Question {
  id?: string;
  text: string;
  type: 'OPEN_TEXT' | 'MULTIPLE_CHOICE' | 'RATING' | 'YES_NO';
  isRequired: boolean;
  order: number;
  options?: QuestionOption[];
  enableSentimentAnalysis?: boolean;
  enableKeywordExtraction?: boolean;
}

interface CustomForm {
  id?: string;
  name: string;
  productId: string;
  status: 'DRAFT' | 'ACTIVE' | 'ARCHIVED';
  createdAt: string;
  updatedAt: string;
  product: {
    id: string;
    name: string;
  };
  questions: Question[];
}

interface FormViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  form: CustomForm | null;
}

const API_BASE_URL = import.meta.env.VITE_API_URL;

// Get Firebase ID token
const getAuthToken = async () => {
  return getIdToken();
};

// Helper function to add auth headers
const getAuthHeaders = async () => {
  const token = await getAuthToken();
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : '',
  };
};

const FormViewModal = ({ isOpen, onClose, form }: FormViewModalProps) => {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const getQuestionTypeLabel = (type: string) => {
    const labels = {
      OPEN_TEXT: 'Open Text',
      MULTIPLE_CHOICE: 'Multiple Choice',
      RATING: 'Rating (1-5)',
      YES_NO: 'Yes/No',
    };
    return labels[type as keyof typeof labels] || type;
  };

  const getQuestionTypeIcon = (type: string) => {
    switch (type) {
      case 'OPEN_TEXT':
        return '📝';
      case 'MULTIPLE_CHOICE':
        return '☑️';
      case 'RATING':
        return '⭐';
      case 'YES_NO':
        return '✅';
      default:
        return '❓';
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      DRAFT: "secondary",
      ACTIVE: "default",
      ARCHIVED: "destructive",
    } as const;

    return (
      <Badge variant={variants[status as keyof typeof variants]}>
        {status.toLowerCase()}
      </Badge>
    );
  };



  const renderQuestionPreview = (question: Question) => {
    switch (question.type) {
      case 'OPEN_TEXT':
        return (
          <div className="space-y-2">
            <div className="text-sm text-muted-foreground">Text input field</div>
            <div className="h-10 bg-muted rounded-md border border-dashed border-muted-foreground/30 flex items-center px-3 text-muted-foreground text-sm">
              Enter your response here...
            </div>
            
            {/* AI Analysis Indicators */}
            {(question.enableSentimentAnalysis || question.enableKeywordExtraction) && (
              <div className="pt-2 border-t border-muted">
                <div className="text-xs font-medium text-muted-foreground mb-2">AI Analysis:</div>
                <div className="space-y-1">
                  {question.enableSentimentAnalysis && (
                    <div className="flex items-center gap-2 text-xs">
                      <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
                      <span className="text-muted-foreground">Sentiment Analysis Enabled</span>
                    </div>
                  )}
                  {question.enableKeywordExtraction && (
                    <div className="flex items-center gap-2 text-xs">
                      <div className="w-3 h-3 bg-primary rounded-full"></div>
                      <span className="text-muted-foreground">Keyword Extraction Enabled</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );

      case 'MULTIPLE_CHOICE':
        return (
          <div className="space-y-2">
            {question.options?.map((option, index) => (
              <div key={index} className="flex items-center gap-2">
                <input type="radio" disabled className="text-primary" />
                <span className="text-sm">{option.text}</span>
              </div>
            )) || (
              <div className="text-sm text-muted-foreground">No options defined</div>
            )}
          </div>
        );

      case 'RATING':
        return (
          <div className="space-y-2">
            <div className="text-sm text-muted-foreground">Rating scale (1-5)</div>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <div key={star} className="w-6 h-6 bg-muted rounded flex items-center justify-center text-xs text-muted-foreground">
                  {star}
                </div>
              ))}
            </div>
          </div>
        );

      case 'YES_NO':
        return (
          <div className="space-y-2">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <input type="radio" disabled className="text-primary" />
                <span className="text-sm">Yes</span>
              </div>
              <div className="flex items-center gap-2">
                <input type="radio" disabled className="text-primary" />
                <span className="text-sm">No</span>
              </div>
            </div>
          </div>
        );

      default:
        return <div className="text-sm text-muted-foreground">Unknown question type</div>;
    }
  };

  if (!form) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start justify-between">
            <div>
              <DialogTitle className="text-2xl">{form.name}</DialogTitle>
              <div className="flex items-center gap-2 mt-2">
                {getStatusBadge(form.status)}
                <span className="text-sm text-muted-foreground">
                  {form.product.name}
                </span>
              </div>
            </div>

          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Form Metadata */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Form Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    Created
                  </div>
                  <div className="font-medium">
                    {new Date(form.createdAt).toLocaleDateString()} at{' '}
                    {new Date(form.createdAt).toLocaleTimeString()}
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    Last Updated
                  </div>
                  <div className="font-medium">
                    {new Date(form.updatedAt).toLocaleDateString()} at{' '}
                    {new Date(form.updatedAt).toLocaleTimeString()}
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <BarChart3 className="h-4 w-4" />
                    Questions
                  </div>
                  <div className="font-medium">
                    {form.questions.length} question{form.questions.length !== 1 ? 's' : ''}
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <User className="h-4 w-4" />
                    Status
                  </div>
                  <div>{getStatusBadge(form.status)}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Questions */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Questions ({form.questions.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {form.questions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No questions defined for this form.
                </div>
              ) : (
                form.questions
                  .sort((a, b) => a.order - b.order)
                  .map((question, index) => (
                    <div key={question.id || index} className="space-y-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="text-2xl">{getQuestionTypeIcon(question.type)}</div>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium">Question {index + 1}</span>
                              <Badge variant="outline">
                                {getQuestionTypeLabel(question.type)}
                              </Badge>
                              {question.isRequired && (
                                <Badge variant="destructive" className="text-xs">
                                  Required
                                </Badge>
                              )}
                            </div>
                            <div className="text-lg">{question.text}</div>
                          </div>
                        </div>
                      </div>

                      <div className="ml-11">
                        {renderQuestionPreview(question)}
                      </div>

                      {index < form.questions.length - 1 && (
                        <Separator className="mt-6" />
                      )}
                    </div>
                  ))
              )}
            </CardContent>
          </Card>



          {/* Actions */}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FormViewModal; 